import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { DashboardAnalyticsService } from '../../../services/analytics/dashboard.analytics';
import type { 
  DashboardMetrics as ServiceDashboardMetrics,
  DashboardChartData as ServiceDashboardChartData,
  DashboardAlerts as ServiceDashboardAlerts
} from '../../../services/analytics/dashboard.analytics';

// Transformation functions to convert service data to Redux state structure
const transformMetrics = (serviceMetrics: ServiceDashboardMetrics): DashboardMetrics => {
  return {
    totalAssets: {
      total: serviceMetrics.totalAssets,
      active: serviceMetrics.activeAssets,
      inactive: serviceMetrics.inactiveAssets
    },
    totalUsers: {
      total: serviceMetrics.totalUsers,
      active: serviceMetrics.activeUsers,
      inactive: serviceMetrics.inactiveUsers
    },
    totalMaintenance: {
      total: serviceMetrics.totalMaintenance,
      active: serviceMetrics.pendingMaintenance,
      inactive: serviceMetrics.completedMaintenance
    },
    totalDispatch: {
      total: serviceMetrics.totalDispatch,
      active: serviceMetrics.pendingDispatch,
      inactive: serviceMetrics.completedDispatch
    },
    // Rate metrics
    assetUtilizationRate: serviceMetrics.assetUtilizationRate,
    maintenanceComplianceRate: serviceMetrics.maintenanceComplianceRate,
    dispatchEfficiencyRate: serviceMetrics.dispatchEfficiencyRate,
    // Geographic metrics
    totalRegions: serviceMetrics.totalRegions,
    totalDistricts: serviceMetrics.totalDistricts,
    totalFacilities: serviceMetrics.totalFacilities,
    // Distribution flow metrics
    totalDistributions: {
      total: serviceMetrics.totalDistributions,
      active: serviceMetrics.inTransitDistributions,
      inactive: serviceMetrics.receivedDistributions + serviceMetrics.rejectedDistributions
    },
    pendingDistributions: serviceMetrics.pendingDistributions,
    inTransitDistributions: serviceMetrics.inTransitDistributions,
    receivedDistributions: serviceMetrics.receivedDistributions,
    rejectedDistributions: serviceMetrics.rejectedDistributions,
    distributionAcceptanceRate: serviceMetrics.distributionAcceptanceRate,
    distributionRejectionRate: serviceMetrics.distributionRejectionRate,
    distributionFlowEfficiency: serviceMetrics.distributionFlowEfficiency,
    // Donor & Batch Analytics metrics
    totalDonors: serviceMetrics.totalDonors,
    activeDonors: serviceMetrics.activeDonors,
    totalBatches: serviceMetrics.totalBatches,
    averageAssetsPerBatch: serviceMetrics.averageAssetsPerBatch,
    // Asset Lifecycle metrics
    averageAssetAge: serviceMetrics.averageAssetAge,
    assetsUnderWarranty: serviceMetrics.assetsUnderWarranty,
    assetsOutOfWarranty: serviceMetrics.assetsOutOfWarranty,
    warrantyComplianceRate: serviceMetrics.warrantyComplianceRate
  };
};

const transformChartData = (serviceChartData: ServiceDashboardChartData): DashboardChartData => {
  // Helper function to convert PieItem[] to ChartDataPoint format
  const convertPieItems = (pieItems: any[]): Array<{ name: string; value: number }> => {
    return pieItems.map((item) => ({
      name: item.label,
      value: item.value,
    }));
  };

  // Helper function to convert time series data to TimeSeriesDataPoint format
  const convertTimeSeriesData = (timeSeriesData: any): Array<{ month: string; value: number }> => {
    if (!timeSeriesData?.labels?.length || !timeSeriesData?.datasets?.length) {
      return [];
    }

    return timeSeriesData.labels.map((label: string, index: number) => ({
      month: label,
      value: timeSeriesData.datasets[0]?.data[index] ?? 0
    }));
  };

  return {
    assetsByCategory: convertPieItems(serviceChartData.assetsByCategory ?? []),
    assetsByLocation: convertPieItems(serviceChartData.assetsByLocation ?? []),
    assetsByStatus: convertPieItems(serviceChartData.assetsByStatus ?? []),
    maintenanceByStatus: convertPieItems(serviceChartData.maintenanceByStatus ?? []),
    monthlyAssetTrends: convertTimeSeriesData(serviceChartData.monthlyAssetTrends),
    maintenanceTrends: convertTimeSeriesData(serviceChartData.maintenanceTrends),
    // Add placeholder data for missing properties
    dispatchTrends: [],
    userActivityTrends: []
  };
};

const transformAlerts = (serviceAlerts: ServiceDashboardAlerts): DashboardAlert[] => {
  return serviceAlerts.criticalAlerts.map(alert => {
    let alertType: 'critical' | 'warning' | 'info';
    if (alert.priority === 'high') {
      alertType = 'critical';
    } else if (alert.priority === 'medium') {
      alertType = 'warning';
    } else {
      alertType = 'info';
    }

    return {
      id: alert.id,
      type: alertType,
      title: alert.title,
      message: alert.description,
      timestamp: alert.timestamp,
      actionable: true
    };
  });
};

// Types - Updated to match service types
export interface DashboardMetrics {
  totalAssets: { total: number; active: number; inactive: number };
  totalUsers: { total: number; active: number; inactive: number };
  totalMaintenance: { total: number; active: number; inactive: number };
  totalDispatch: { total: number; active: number; inactive: number };
  // Rate metrics
  assetUtilizationRate: number;
  maintenanceComplianceRate: number;
  dispatchEfficiencyRate: number;
  // Geographic metrics
  totalRegions: number;
  totalDistricts: number;
  totalFacilities: number;
  // Distribution flow metrics
  totalDistributions: { total: number; active: number; inactive: number };
  pendingDistributions: number;
  inTransitDistributions: number;
  receivedDistributions: number;
  rejectedDistributions: number;
  distributionAcceptanceRate: number;
  distributionRejectionRate: number;
  distributionFlowEfficiency: number;
  // Donor & Batch Analytics metrics
  totalDonors: number;
  activeDonors: number;
  totalBatches: number;
  averageAssetsPerBatch: number;
  // Asset Lifecycle metrics
  averageAssetAge: number;
  assetsUnderWarranty: number;
  assetsOutOfWarranty: number;
  warrantyComplianceRate: number;
}

export interface DashboardChartData {
  assetsByCategory: Array<{ name: string; value: number }>;
  assetsByLocation: Array<{ name: string; value: number }>;
  assetsByStatus: Array<{ name: string; value: number }>;
  maintenanceByStatus: Array<{ name: string; value: number }>;
  monthlyAssetTrends: Array<{ month: string; value: number }>;
  maintenanceTrends: Array<{ month: string; value: number }>;
  dispatchTrends: Array<{ month: string; value: number }>;
  userActivityTrends: Array<{ month: string; value: number }>;
}

export interface DashboardAlert {
  id: string;
  type: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: string;
  actionable?: boolean;
}

export interface DashboardFilters {
  timePeriod: string;
  location?: string;
  category?: string;
  autoRefresh: boolean;
}

export interface DashboardState {
  metrics: DashboardMetrics | null;
  chartData: DashboardChartData | null;
  alerts: DashboardAlert[];
  filters: DashboardFilters;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
  availableLocations: string[];
  availableCategories: string[];
}

// Initial state
const initialState: DashboardState = {
  metrics: null,
  chartData: null,
  alerts: [],
  filters: {
    timePeriod: '30d',
    autoRefresh: false
  },
  loading: false,
  error: null,
  lastUpdated: null,
  availableLocations: [],
  availableCategories: []
};

// Async thunks
export const fetchDashboardMetrics = createAsyncThunk(
  'dashboard/fetchMetrics',
  async (timePeriod?: string) => {
    const dashboardService = DashboardAnalyticsService.getInstance();
    const serviceMetrics = await dashboardService.getDashboardMetrics();
    return transformMetrics(serviceMetrics);
  }
);

export const fetchDashboardChartData = createAsyncThunk(
  'dashboard/fetchChartData',
  async (timePeriod?: string) => {
    const dashboardService = DashboardAnalyticsService.getInstance();
    const serviceChartData = await dashboardService.getDashboardChartData();
    return transformChartData(serviceChartData);
  }
);

export const fetchDashboardAlerts = createAsyncThunk(
  'dashboard/fetchAlerts',
  async () => {
    const dashboardService = DashboardAnalyticsService.getInstance();
    const serviceAlerts = await dashboardService.getDashboardAlerts();
    return transformAlerts(serviceAlerts);
  }
);

export const refreshDashboardData = createAsyncThunk(
  'dashboard/refreshData',
  async (timePeriod: string, { dispatch }) => {
    // Fetch all dashboard data in parallel
    const [metrics, chartData, alerts] = await Promise.all([
      dispatch(fetchDashboardMetrics(timePeriod)).unwrap(),
      dispatch(fetchDashboardChartData(timePeriod)).unwrap(),
      dispatch(fetchDashboardAlerts()).unwrap()
    ]);

    return { metrics, chartData, alerts };
  }
);

// Slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<DashboardFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setTimePeriod: (state, action: PayloadAction<string>) => {
      state.filters.timePeriod = action.payload;
    },
    setLocation: (state, action: PayloadAction<string | undefined>) => {
      state.filters.location = action.payload;
    },
    setCategory: (state, action: PayloadAction<string | undefined>) => {
      state.filters.category = action.payload;
    },
    setAutoRefresh: (state, action: PayloadAction<boolean>) => {
      state.filters.autoRefresh = action.payload;
    },
    dismissAlert: (state, action: PayloadAction<string>) => {
      state.alerts = state.alerts.filter(alert => alert.id !== action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
    setAvailableLocations: (state, action: PayloadAction<string[]>) => {
      state.availableLocations = action.payload;
    },
    setAvailableCategories: (state, action: PayloadAction<string[]>) => {
      state.availableCategories = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Fetch metrics
    builder
      .addCase(fetchDashboardMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardMetrics.fulfilled, (state, action) => {
        state.loading = false;
        state.metrics = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchDashboardMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch dashboard metrics';
      });

    // Fetch chart data
    builder
      .addCase(fetchDashboardChartData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardChartData.fulfilled, (state, action) => {
        state.loading = false;
        state.chartData = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchDashboardChartData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch dashboard chart data';
      });

    // Fetch alerts
    builder
      .addCase(fetchDashboardAlerts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardAlerts.fulfilled, (state, action) => {
        state.loading = false;
        state.alerts = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchDashboardAlerts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch dashboard alerts';
      });

    // Refresh all data
    builder
      .addCase(refreshDashboardData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.metrics = action.payload.metrics;
        state.chartData = action.payload.chartData;
        state.alerts = action.payload.alerts;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(refreshDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to refresh dashboard data';
      });
  }
});

// Actions
export const {
  setFilters,
  setTimePeriod,
  setLocation,
  setCategory,
  setAutoRefresh,
  dismissAlert,
  clearError,
  setAvailableLocations,
  setAvailableCategories
} = dashboardSlice.actions;

// Selectors
export const selectDashboardMetrics = (state: { dashboard: DashboardState }) => state.dashboard.metrics;
export const selectDashboardChartData = (state: { dashboard: DashboardState }) => state.dashboard.chartData;
export const selectDashboardAlerts = (state: { dashboard: DashboardState }) => state.dashboard.alerts;
export const selectDashboardFilters = (state: { dashboard: DashboardState }) => state.dashboard.filters;
export const selectDashboardLoading = (state: { dashboard: DashboardState }) => state.dashboard.loading;
export const selectDashboardError = (state: { dashboard: DashboardState }) => state.dashboard.error;
export const selectDashboardLastUpdated = (state: { dashboard: DashboardState }) => state.dashboard.lastUpdated;
export const selectAvailableLocations = (state: { dashboard: DashboardState }) => state.dashboard.availableLocations;
export const selectAvailableCategories = (state: { dashboard: DashboardState }) => state.dashboard.availableCategories;

export default dashboardSlice.reducer;
